import { Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import {
  getGetBrandQueryKey,
  getListBrandsQueryKey,
  useDeleteBrand,
} from "../../api/generated/endpoints/teams/teams";
import type { Brand } from "../../api/generated/model";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../ErrorAlert";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { useRefetchOnRefusal } from "../refusal";

/** Asks, then deletes the brand and returns to the team's brands (A3), or calls `onDeleted`. */
export function DeleteBrand(props: {
  teamId: string;
  brand: Brand;
  onCancel: () => void;
  /** Where the page goes once the brand is gone; the team's Brands page when not given. */
  onDeleted?: (() => Promise<void>) | undefined;
}) {
  const { teamId, brand, onCancel, onDeleted } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const onError = useRefetchOnRefusal(getListBrandsQueryKey(teamId));
  const remove = useDeleteBrand({
    mutation: {
      onError,
      onSuccess: async () => {
        // The scope first: the brand route's gate reads it, and the brand is gone.
        await refreshAccessScope(queryClient);
        queryClient.removeQueries({ queryKey: getGetBrandQueryKey(teamId, brand.id) });
        await queryClient.invalidateQueries({ queryKey: getListBrandsQueryKey(teamId) });
        if (onDeleted !== undefined) await onDeleted();
        else await navigate({ to: "/teams/$teamId/brands", params: { teamId }, replace: true });
      },
    },
  });
  return (
    <ConfirmModal
      opened
      title={`Delete ${brand.name}?`}
      confirmLabel="Delete brand"
      onConfirm={() => remove.mutate({ teamId, brandId: brand.id })}
      onCancel={onCancel}
      pending={remove.isPending}
      error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
    >
      <Text size="sm">
        Its products and folders go with it, and nobody in the team can open it again.
      </Text>
    </ConfirmModal>
  );
}
